/* Copyright (c) 2020-present CloudIO Inc */

import React, { memo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { makeStyles } from '@mui/styles';
import { atom, useStateXAtomSetter } from '@cloudio-saas/statex';
import {
  Alert,
  AlertProps,
  Button,
  IconButton,
  Theme,
  Typography,
} from '@mui/material';
import zIndex from 'constants/ZIndex';
import Icon from 'icons/Icon';
import { usePlatform } from './PlatformHooks';
import { getProfileValue } from 'root/useUser';

const transition = { ease: 'linear', duration: 0.3 };

export interface NoteAction {
  label: string;
  callback: () => void;
}

export interface Note {
  id: string;
  type: AlertProps['severity'];
  msg: string;
  timeoutMs?: number;
  count?: number;
  action?: NoteAction;
  duplicateIds: string[];
}

export const notificationPath = ['notification'];

export const notificationAtom = atom<Note[]>({
  path: notificationPath,
  defaultValue: [],
});

const useStyles = makeStyles(() => ({
  li: {
    minWidth: '300px',
    maxWidth: '880px',
    position: 'relative',
    marginTop: 10,
  },
  body: {
    whiteSpace: 'pre-wrap',
    maxWidth: '780px',
    maxHeight: 300,
    overflow: 'auto',
    userSelect: 'text',
  },
}));

function NotificationItem({ item }: { item: Note }) {
  const classes = useStyles();
  const set = useStateXAtomSetter(notificationAtom);
  const platform = usePlatform();

  const profileTimeOut = Number(
    getProfileValue(platform, platform.appUid, 'DEFAULT_ERROR_MSG_TIMEOUT'),
  );

  useEffect(() => {
    if (item.timeoutMs !== -1) {
      const _timeOut =
        item.timeoutMs ??
        (!Number.isNaN(profileTimeOut) && profileTimeOut > 0
          ? profileTimeOut
          : item.type !== 'error'
          ? item.type === 'warning'
            ? 30000
            : 10000
          : undefined);
      if (_timeOut === undefined) return;
      const timeout = setTimeout(() => {
        set((items) => items.filter((i) => i.id !== item.id));
      }, _timeOut);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [item, profileTimeOut, set]);

  const closeIcon = (
    <IconButton
      onClick={() => {
        set((items) => items.filter((i) => i.id !== item.id));
      }}
      data-testid="CloseIcon"
    >
      <span aria-hidden="false" style={{ display: 'none' }}>
        Close Notification
      </span>
      <Icon iconName="times" color="#fff" />
    </IconButton>
  );

  return (
    <motion.li
      className={classes.li}
      layout="position"
      transition={transition}
      initial={{ opacity: 0, y: 100 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0 }}
      data-testid="notification"
    >
      <Alert
        onClose={() => {
          if (platform.isRecording()) {
            platform.recordLine('', 'ui.close();');
          }
          set((items) => items.filter((i) => i.id !== item.id));
        }}
        elevation={6}
        variant="filled"
        severity={item.type}
        classes={{ message: classes.body }}
        data-testid="notification-alert"
        sx={{ '& button > svg': { pointerEvents: 'none' } }}
        action={
          item.action ? (
            <>
              <Button
                color="inherit"
                onClick={() => {
                  set((items) => items.filter((i) => i.id !== item.id));
                  item.action?.callback();
                }}
                size="small"
                sx={{ whiteSpace: 'nowrap' }}
              >
                {item.action.label}
              </Button>
              {closeIcon}
            </>
          ) : (
            closeIcon
          )
        }
      >
        {item.msg}
        {item.count ? ` (${item.count} messages)` : ''}
      </Alert>
    </motion.li>
  );
}

const useClearAllStyles = makeStyles((theme: Theme) => ({
  clearAll: {
    color: theme.palette.text.secondary,
    position: 'fixed',
    bottom: 0,
    right: 20,
    lineHeight: '40px',
    textDecoration: 'none',
    zIndex: zIndex.Notification,
  },
}));

function ClearAllLink({ count }: { count: number }) {
  const classes = useClearAllStyles();
  const setNotifications = useStateXAtomSetter(notificationAtom);
  const platform = usePlatform();

  return (
    <Typography
      data-testid="clearall-notifications"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (platform.isRecording()) {
          platform.recordLine(
            '',
            "await ui.click({ testid: 'clearall-notifications' });",
          );
        }
        setNotifications([]);
      }}
      className={classes.clearAll}
      sx={{
        bgcolor: 'background.default',
        pl: 4,
        pr: 4,
        m: 1,
        cursor: 'pointer',
        borderRadius: '20px',
      }}
    >
      Clear All {count} Notifications
    </Typography>
  );
}

export const ClearAll = memo(ClearAllLink);

export default memo(NotificationItem);
